import {
  Error as ErrorIcon,
  MenuBook as MenuBookIcon,
  Refresh as RetryIcon,
} from '../icons';
import React, { useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CurrencyDisplay from '../currency/CurrencyDisplay';
import { usePublicTheme } from '../../contexts/PublicThemeContext';

const PaymentFailedCard = ({ payment, order, onRetryPayment, onContactSupport, onGoToMenu }) => {
  const navigate = useNavigate();
  const { primaryColor, updateMerchantTheme } = usePublicTheme();

  // Update merchant theme when order data is available
  useEffect(() => {
    if (order?.merchant?.settings) {
      updateMerchantTheme(order.merchant.settings);
    }
  }, [order?.merchant?.settings, updateMerchantTheme]);
  const getHoverColor = (color, action = 'darken', amount = 15) => {
    // Simple color manipulation - convert hex to RGB, adjust, convert back
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    let newR, newG, newB;
    if (action === 'darken') {
      newR = Math.max(0, r - amount);
      newG = Math.max(0, g - amount);
      newB = Math.max(0, b - amount);
    } else {
      newR = Math.min(255, r + amount);
      newG = Math.min(255, g + amount);
      newB = Math.min(255, b + amount);
    }

    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const handleRetryPayment = () => {
    if (onRetryPayment) {
      onRetryPayment(payment, order);
    }
  };

  const handleBackToCart = () => {
    if (onGoToMenu) {
      onGoToMenu();
    } else {
      navigate('/menu');
    }
  };

  const getErrorMessage = (payment) => {
    if (payment?.notes) {
      return payment.notes;
    }

    // Common error messages based on status or error codes
    const errorMessages = {
      'insufficient_funds': 'Insufficient funds in your account',
      'card_declined': 'Your card was declined by the bank',
      'expired_card': 'Your card has expired',
      'invalid_card': 'Invalid card information',
      'network_error': 'Network connection error occurred',
      'timeout': 'Payment request timed out',
      'processing_error': 'Payment processing error occurred'
    };

    return errorMessages[payment?.error_code] || 'Payment could not be processed. Please try again.';
  };

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 500 }, mx: 'auto', p: { xs: 1.5, sm: 2 } }}>
      {/* Failed Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <ErrorIcon
          sx={{
            fontSize: { xs: 60, sm: 80 },
            color: '#F44336',
            mb: 2
          }}
        />
        <Typography variant="h5" gutterBottom sx={{ color: '#F44336', fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Payment Failed
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We couldn't process your payment. Please try again.
        </Typography>
      </Box>

      {/* Error Alert */}
      <Alert
        severity="error"
        sx={{ mb: 3 }}
        icon={<ErrorIcon />}
      >
        <Typography variant="body2">
          <strong>Error:</strong> {getErrorMessage(payment)}
        </Typography>
      </Alert>

      {/* Payment Details */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Payment Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Transaction ID
            </Typography>
            <Typography variant="body2" noWrap>
              {payment?.transaction_id || payment?.id || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip
                label="failed"
                size="small"
                sx={{
                  bgcolor: '#F44336',
                  color: 'white',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Order Details */}
      {order && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Order Details
          </Typography>

          {/* Order Number */}
          <Box sx={{
            mb: 3,
            p: 2,
            textAlign: 'center'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Order Number
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000000',
                fontFamily: 'monospace',
                letterSpacing: 1,
                mt: 0.5
              }}
            >
              {order.order_number || 'N/A'}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Table
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {order.table_number || order.table?.name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Total Amount
              </Typography>
              <CurrencyDisplay
                amount={payment?.amount || order.total || 0}
                variant="body2"
                fontWeight="medium"
              />
            </Grid>
          </Grid>

          {/* Order Items */}
          {order.items && order.items.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Items ({order.items.length})
              </Typography>
              <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                {order.items.map((item, index) => (
                  <ListItem key={index} divider={index < order.items.length - 1}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            {item.quantity}x {item.name}
                          </Typography>
                          <CurrencyDisplay
                            amount={parseFloat(item.total)}
                            variant="body2"
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<RetryIcon />}
          onClick={handleRetryPayment}
          fullWidth
          sx={{
            bgcolor: primaryColor,
            '&:hover': {
              bgcolor: getHoverColor(primaryColor, 'darken', 15)
            },
            fontWeight: 600
          }}
        >
          TRY AGAIN
        </Button>
        <Button
          variant="outlined"
          startIcon={<MenuBookIcon />}
          onClick={handleBackToCart}
          fullWidth
          sx={{
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: getHoverColor(primaryColor, 'darken', 15),
              bgcolor: getHoverColor(primaryColor, 'lighten', 90),
              color: getHoverColor(primaryColor, 'darken', 15)
            },
            fontWeight: 600
          }}
        >
          BACK TO MENU
        </Button>
      </Box>

      {/* Help Information */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#FFF3E0', borderRadius: 1, border: '1px solid #FFCC02' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          • Check your payment details and try again<br />
          • Ensure you have sufficient funds<br />
          • Contact support if the issue persists
        </Typography>
      </Box>

      {/* Footer Note */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Your order is saved. You can retry payment at any time.
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentFailedCard;
